<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

// Campanha que já saiu leva junto, ao ser excluída, os destinatários com status
// e o contador do relatório. O aviso precisa dizer quanto histórico some, e
// lembrar que pausar para o envio sem apagar nada.
const STARTED_DRIP = ['running', 'paused', 'completed', 'failed', 'cancelled'];
const hasStarted = campaign => {
  if (!campaign) return false;
  if (campaign.is_drip) return STARTED_DRIP.includes(campaign.drip_status);
  return ['completed', 'processing'].includes(campaign.campaign_status);
};

const historyCount = ref(null);
const historyState = ref('idle'); // 'idle' | 'loading' | 'ready' | 'error'

const loadHistory = async campaign => {
  historyCount.value = null;
  if (!hasStarted(campaign)) {
    historyState.value = 'idle';
    return;
  }
  historyState.value = 'loading';
  try {
    const { data } = await CampaignsAPI.show(campaign.id);
    if (props.selectedCampaign?.id !== campaign.id) return;
    historyCount.value = Math.max(
      Number(data?.recipients_count) || 0,
      Number(data?.sent_count) || 0
    );
    historyState.value = 'ready';
  } catch (error) {
    if (props.selectedCampaign?.id === campaign.id) historyState.value = 'error';
  }
};

watch(
  () => props.selectedCampaign,
  campaign => loadHistory(campaign),
  { immediate: true }
);

const isRunningDrip = computed(
  () =>
    props.selectedCampaign?.is_drip &&
    props.selectedCampaign?.drip_status === 'running'
);

const description = computed(() => {
  const parts = [];
  if (historyState.value === 'loading') {
    parts.push(t('CAMPAIGN.CONFIRM_DELETE.HISTORY_LOADING'));
  } else if (historyState.value === 'ready' && historyCount.value === 1) {
    parts.push(t('CAMPAIGN.CONFIRM_DELETE.HISTORY_DESCRIPTION_ONE'));
  } else if (historyState.value === 'ready' && historyCount.value > 1) {
    parts.push(
      t('CAMPAIGN.CONFIRM_DELETE.HISTORY_DESCRIPTION', {
        count: historyCount.value.toLocaleString(),
      })
    );
  } else if (historyState.value === 'error') {
    parts.push(t('CAMPAIGN.CONFIRM_DELETE.HISTORY_UNKNOWN'));
  } else {
    parts.push(t('CAMPAIGN.CONFIRM_DELETE.DESCRIPTION'));
  }
  if (isRunningDrip.value) parts.push(t('CAMPAIGN.CONFIRM_DELETE.PAUSE_HINT'));
  return parts.join(' ');
});

const deleteCampaign = async id => {
  if (!id) return;

  try {
    await store.dispatch('campaigns/delete', id);
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.ERROR_MESSAGE'));
  }
};

const handleDialogConfirm = async () => {
  await deleteCampaign(props.selectedCampaign.id);
  dialogRef.value?.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="t('CAMPAIGN.CONFIRM_DELETE.TITLE')"
    :description="description"
    :confirm-button-label="t('CAMPAIGN.CONFIRM_DELETE.CONFIRM')"
    :disable-confirm-button="historyState === 'loading'"
    @confirm="handleDialogConfirm"
  />
</template>
